import React, { useContext, useEffect, useState, useRef } from 'react'
import cn from 'classnames'
import { Field, Button } from '@insight/toolkit-react'
import { t, isDesktop } from '@insight/toolkit-utils'
import { AIDeviceTooltip } from './components/AIDeviceTooltip'
import { HIDE_LOGGEDOUT_PRICING_BY_SALESORG, IN_STOCK_STATUS_FIELD } from '../../constants'
import { SearchContext } from '../../context/SearchContext'

// Filters out facets that are already represented elsewhere in the UI — e.g. the
// in-stock status facet, which is rendered via the dedicated "Show in stock only"
// checkbox instead of as a regular dynamic facet group.
const parseFacets = (facets = []) =>
  facets.filter((facet) => facet.state.field !== IN_STOCK_STATUS_FIELD)

// Facet keys that support typeahead search within values
const SEARCHABLE_FACET_KEYS_PREFIX = ['ini_mfr_name', 'ec_brand', 'topics'];

const normalizeComparableText = (value = '') =>
  value
    .toString()
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[''`´]/g, "'")
    .replace(/'/g, '')
    .replace(/\s+/g, ' ')
    .toLowerCase()

const labelsMatch = (facetLabel, facetTranslation) =>
  normalizeComparableText(facetLabel) ===
  normalizeComparableText(facetTranslation)

// ---------------------------------------------------------------------------
// Individual facet type renderers
// ---------------------------------------------------------------------------

/**
 * RegularFacet — renders checkboxes for each facet value.
 * Uses controller.toggleSelect(value) for selection/deselection.
 */
const RegularFacetGroup = ({ controller }) => {
  const [state, setState] = useState(controller.state)
  const [expanded, setExpanded] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')

  useEffect(() => {
    // Read current state immediately in case it changed between render and effect
    setState(controller.state)
    return controller.subscribe(() => setState(controller.state))
  }, [controller])

  const field = state.field
  const displayName = state.displayName ?? state.facetId
  const isSearchableFacet = SEARCHABLE_FACET_KEYS_PREFIX.some(
    (key) => field?.toLowerCase()?.includes(key)
  )
  const hideLoggedOutPricing = window?.flags?.[HIDE_LOGGEDOUT_PRICING_BY_SALESORG]
  const desktopView = isDesktop()
  const isAIDeviceFacet = labelsMatch(displayName, t('AI Device Type'))

  // Filter values with count > 0
  const filteredValues = state.values.filter((v) => v.numberOfResults > 0)

  // Apply typeahead filter
  const displayValues = searchTerm
    ? filteredValues.filter((v) =>
        v.value.toUpperCase().includes(searchTerm.toUpperCase())
      )
    : filteredValues

  if (filteredValues.length === 0) return null

  const showMore = displayValues.length > 5 && !expanded
  const showLess = displayValues.length > 5 && expanded
  const visibleValues = showMore ? displayValues.slice(0, 5) : displayValues

  // Price facet hiding
  const isPriceFacet = displayName === 'Price' || field?.includes('price')
  if (hideLoggedOutPricing && isPriceFacet) return null

  return (
    <section className="c-search-filter__group o-grid">
      <div className="c-search-filter__group__label o-grid__item u-1/1">
        {isAIDeviceFacet && !desktopView && <AIDeviceTooltip />}
        {t(displayName)}
        {isAIDeviceFacet && desktopView && <AIDeviceTooltip />}
      </div>
      {isSearchableFacet && (
        <div className="o-grid__item u-1/1">
          <label className="u-hide-visually" htmlFor={`facet-search-${field}`}>
            {`Search by ${displayName}`}
          </label>
          <input
            id={`facet-search-${field}`}
            className="c-search-filter__group__input"
            placeholder={`Search by ${displayName}`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      )}
      {searchTerm && displayValues.length === 0 ? (
        <p className="c-search-filter__group__none">No results</p>
      ) : (
        <ul className="c-search-filter__group__container o-grid__item u-1/1 o-grid">
          {visibleValues.map((value) => {
            const key = `${field}:${value.value}`
            const isSelected = value.state === 'selected'
            return (
              <li key={key} className="o-grid__item u-1/1">
                <Field
                  name={key}
                  fieldComponent="Checkbox"
                  checkboxLabel={
                    value.numberOfResults
                      ? `${value.value} (${value.numberOfResults})`
                      : value.value
                  }
                  checked={isSelected}
                  onChange={() => controller.toggleSelect(value)}
                />
              </li>
            )
          })}
        </ul>
      )}
      {showMore && (
        <div className="o-grid__item u-1/1">
          <Button
            className="c-search-filter__group__show-more u-text-bold"
            icon="arrow-down"
            color="inline-link"
            onClick={() => setExpanded(true)}
          >
            {t('Show more')}
          </Button>
        </div>
      )}
      {showLess && (
        <div className="o-grid__item u-1/1">
          <Button
            className="c-search-filter__group__show-less u-text-bold"
            icon="arrow-up"
            color="inline-link"
            onClick={() => setExpanded(false)}
          >
            {t('Show less')}
          </Button>
        </div>
      )}
    </section>
  )
}

// ─── SliderFacetGroup ─────────────────────────────────────────────────────────

const SliderFacetGroup = ({ controller, displayName }) => {
  const [state, setState] = useState(controller.state)
  const [minInput, setMinInput] = useState('')
  const [maxInput, setMaxInput] = useState('')
  // Separate slider position state — only updated on slider drag or Go click
  const [sliderMin, setSliderMin] = useState(null)
  const [sliderMax, setSliderMax] = useState(null)

  const { error: searchError, isSearching } = useContext(SearchContext)

  // Saved on mouseDown — guaranteed to be the value BEFORE any drag begins.
  // Not updated again until the next mouseDown, so intermediate drag events
  // cannot overwrite it.
  const preInteractionRef = useRef({ min: null, max: null })
  const pendingRef = useRef(false)
  const searchErrorRef = useRef(searchError)

  useEffect(() => {
    searchErrorRef.current = searchError
  }, [searchError])

  useEffect(() => {
    setState(controller.state)
    return controller.subscribe(() => setState(controller.state))
  }, [controller])

  // Sync slider position from manualRange (URL restore, external state change).
  // Skip while a request is in-flight so Coveo's optimistic manualRange update
  // does not overwrite the pre-interaction values saved in preInteractionRef.
  useEffect(() => {
    if (pendingRef.current) return
    if (state.manualRange) {
      // Active range — sync slider and inputs to the current selection
      setMinInput(String(state.manualRange.start))
      setMaxInput(String(state.manualRange.end))
      setSliderMin(state.manualRange.start)
      setSliderMax(state.manualRange.end)
    } else {
      // manualRange cleared (e.g. breadcrumb chip deselected) — reset to unselected
      setMinInput('')
      setMaxInput('')
      setSliderMin(null)
      setSliderMax(null)
    }
  }, [state.manualRange])

  // isSearching transitions true→false on every request completion (success or failure).
  // This fires reliably regardless of whether the error object reference changed,
  // fixing the "second drag doesn't reset" issue caused by a stale error reference.
  useEffect(() => {
    // Only act when a search we triggered just completed
    if (isSearching || !pendingRef.current) return
    pendingRef.current = false
    // Read error via ref — avoids stale closure when isSearching and error
    // change in the same render cycle
    if (searchErrorRef.current) {
      const { min, max } = preInteractionRef.current
      setSliderMin(min)
      setSliderMax(max)
      setMinInput(min !== null ? String(min) : '')
      setMaxInput(max !== null ? String(max) : '')
    }
  }, [isSearching])

  const domain = state.domain
  if (!domain) return null

  const domainMin = domain.min
  const domainMax = domain.max

  // Slider position — from explicit slider/Go interaction, else manualRange, else domain bounds
  const currentSliderMin = sliderMin ?? (state.manualRange?.start ?? domainMin)
  const currentSliderMax = sliderMax ?? (state.manualRange?.end ?? domainMax)

  const clampedMin = Math.max(domainMin, Math.min(currentSliderMin, domainMax))
  const clampedMax = Math.max(domainMin, Math.min(currentSliderMax, domainMax))

  // Called once on mouseDown/touchStart — before any drag events fire.
  // This is the only place preInteractionRef is written, so repeated
  // drag events cannot corrupt the saved pre-interaction value.
  const handleSliderMouseDown = () => {
    preInteractionRef.current = {
      min: sliderMin ?? (state.manualRange?.start ?? domainMin),
      max: sliderMax ?? (state.manualRange?.end ?? domainMax),
    }
    pendingRef.current = true
  }

  const applyRange = (start, end) => {
    const safeStart = Math.max(domainMin, Math.min(start, domainMax))
    const safeEnd = Math.max(safeStart, Math.min(end, domainMax))
    controller.setRanges([{
      start: safeStart,
      end: safeEnd,
      endInclusive: true,
      state: 'selected',
    }])
  }

  // ─── Slider handlers — update slider position AND input boxes immediately ──

  const handleSliderMinChange = (e) => {
    const val = Number(e.target.value)
    const newMin = Math.min(val, clampedMax)
    setSliderMin(newMin)
    setMinInput(String(newMin))
    applyRange(newMin, clampedMax)
  }

  const handleSliderMaxChange = (e) => {
    const val = Number(e.target.value)
    const newMax = Math.max(val, clampedMin)
    setSliderMax(newMax)
    setMaxInput(String(newMax))
    applyRange(clampedMin, newMax)
  }

  // ─── Input box handlers — only update text, NOT the slider ────────────────

  const handleMinInputChange = (e) => {
    setMinInput(e.target.value.replace(/[^\d.]/g, ''))
  }

  const handleMaxInputChange = (e) => {
    setMaxInput(e.target.value.replace(/[^\d.]/g, ''))
  }

  // ─── Go button — update slider position AND apply range ──────────────────

  const handleInputSubmit = (e) => {
    e?.preventDefault?.()
    const min = minInput !== '' ? Number(minInput) : domainMin
    const max = maxInput !== '' ? Number(maxInput) : domainMax
    if (isNaN(min) || isNaN(max)) return
    const safeStart = Math.max(domainMin, Math.min(min, domainMax))
    const safeEnd = Math.max(safeStart, Math.min(max, domainMax))
    // Snapshot the effective *current* bounds (falls back through manualRange/domain when the
    // slider was never dragged) rather than the raw sliderMin/sliderMax, which may still be null.
    preInteractionRef.current = { min: clampedMin, max: clampedMax }
    pendingRef.current = true
    // Update slider position to reflect the submitted values
    setSliderMin(safeStart)
    setSliderMax(safeEnd)
    // Sync input boxes to clamped values
    setMinInput(String(safeStart))
    setMaxInput(String(safeEnd))
    applyRange(safeStart, safeEnd)
  }

  const range = domainMax - domainMin || 1
  const leftPercent = ((clampedMin - domainMin) / range) * 100
  const rightPercent = ((clampedMax - domainMin) / range) * 100

  return (
    <section className="c-search-filter__group o-grid">
      <div className="c-search-filter__group__label o-grid__item u-1/1">
        {t(displayName)}
      </div>
      <div className="c-numeric-range-slider o-grid__item u-1/1">
        <div className="c-numeric-range-slider__track">
          <div
            className="c-numeric-range-slider__track-fill"
            style={{ left: `${leftPercent}%`, width: `${rightPercent - leftPercent}%` }}
          />
          <input
            type="range"
            className="c-numeric-range-slider__input c-numeric-range-slider__input--min"
            min={domainMin}
            max={domainMax}
            step={1}
            value={clampedMin}
            onChange={handleSliderMinChange}
            onMouseDown={handleSliderMouseDown}
            onTouchStart={handleSliderMouseDown}
            aria-label={`${displayName} minimum`}
          />
          <input
            type="range"
            className="c-numeric-range-slider__input c-numeric-range-slider__input--max"
            min={domainMin}
            max={domainMax}
            step={1}
            value={clampedMax}
            onChange={handleSliderMaxChange}
            onMouseDown={handleSliderMouseDown}
            onTouchStart={handleSliderMouseDown}
            aria-label={`${displayName} maximum`}
          />
        </div>
        <form className="c-numeric-range-slider__inputs" onSubmit={handleInputSubmit}>
          <div className="c-numeric-range-slider__field">
            <Field
              fieldComponent="Text"
              aria-label={t('Minimum')}
              name={`${state.field}-min`}
              placeholder={`${domainMin}`}
              maxLength={10}
              value={minInput}
              onChange={handleMinInputChange}
            />
          </div>
          <span className="c-numeric-range-slider__separator">{t('to')}</span>
          <div className="c-numeric-range-slider__field">
            <Field
              fieldComponent="Text"
              aria-label={t('Maximum')}
              name={`${state.field}-max`}
              placeholder={`${domainMax}`}
              maxLength={10}
              value={maxInput}
              onChange={handleMaxInputChange}
            />
          </div>
          <Button color="primary" onClick={handleInputSubmit} size="small">
            {t('Go')}
          </Button>
        </form>
      </div>
    </section>
  )
}

// ─── RangesFacetGroup ─────────────────────────────────────────────────────────

const RangesFacetGroup = ({ controller, displayName }) => {
  const [state, setState] = useState(controller.state)
  const [minInput, setMinInput] = useState('')
  const [maxInput, setMaxInput] = useState('')

  useEffect(() => {
    setState(controller.state)
    return controller.subscribe(() => setState(controller.state))
  }, [controller])

  useEffect(() => {
    if (state.manualRange) {
      setMinInput(String(state.manualRange.start))
      setMaxInput(String(state.manualRange.end))
    }
  }, [state.manualRange])

  const filteredValues = state.values.filter((v) => v.numberOfResults > 0)
  if (filteredValues.length === 0) return null

  const isPriceInputFacet = state.facetId === 'ec_price' || state.field === 'ec_price'

  const handleMinInputChange = (e) => {
    setMinInput(e.target.value.replace(/[^\d.]/g, ''))
  }

  const handleMaxInputChange = (e) => {
    setMaxInput(e.target.value.replace(/[^\d.]/g, ''))
  }

  const handleInputSubmit = (e) => {
    e?.preventDefault?.()
    const min = minInput !== '' ? Number(minInput) : 0
    const max = maxInput !== '' ? Number(maxInput) : 999999
    if (isNaN(min) || isNaN(max)) return
    const safeStart = Math.max(0, min)
    const safeEnd = Math.max(safeStart, Math.max(0, max))
    controller.setRanges([{
      start: safeStart,
      end: safeEnd,
      endInclusive: true,
      state: 'selected',
    }])
  }

  return (
    <section className="c-search-filter__group o-grid">
      <div className="c-search-filter__group__label o-grid__item u-1/1">
        {t(displayName)}
      </div>
      {isPriceInputFacet && (
        <div className="c-numeric-range-slider o-grid__item u-1/1">
          <form className="c-numeric-range-slider__inputs" onSubmit={handleInputSubmit}>
            <div className="c-numeric-range-slider__field">
              <Field
                fieldComponent="Text"
                aria-label={t('Minimum')}
                name={`${state.field}-min`}
                placeholder="Min"
                maxLength={10}
                value={minInput}
                onChange={handleMinInputChange}
              />
            </div>
            <span className="c-numeric-range-slider__separator">{t('to')}</span>
            <div className="c-numeric-range-slider__field">
              <Field
                fieldComponent="Text"
                aria-label={t('Maximum')}
                name={`${state.field}-max`}
                placeholder="Max"
                maxLength={10}
                value={maxInput}
                onChange={handleMaxInputChange}
              />
            </div>
            <Button color="primary" onClick={handleInputSubmit} size="small">
              {t('Go')}
            </Button>
          </form>
        </div>
      )}
      <ul className="c-search-filter__group__container o-grid__item u-1/1 o-grid">
        {filteredValues.map((value) => {
          const key = `${state.field}:${value.start}-${value.end}`
          const isSelected = value.state === 'selected'
          const label = `${value.start} - ${value.end}`
          return (
            <li key={key} className="o-grid__item u-1/1">
              <Field
                name={key}
                fieldComponent="Checkbox"
                checkboxLabel={
                  value.numberOfResults
                    ? `${label} (${value.numberOfResults})`
                    : label
                }
                checked={isSelected}
                onChange={() => controller.toggleSelect(value)}
              />
            </li>
          )
        })}
      </ul>
    </section>
  )
}

// ─── NumericFacetGroup ────────────────────────────────────────────────────────

const NumericFacetGroup = ({ controller }) => {
  const [state, setState] = useState(controller.state)

  useEffect(() => {
    setState(controller.state)
    return controller.subscribe(() => setState(controller.state))
  }, [controller])

  const displayName = state.displayName ?? state.facetId
  const hideLoggedOutPricing = window?.flags?.[HIDE_LOGGEDOUT_PRICING_BY_SALESORG]
  const isPriceFacet = displayName === 'Price' || state.field?.includes('price')
  if (hideLoggedOutPricing && isPriceFacet) return null

  const hasDomain = !!state.domain
  const hasValues = state.values.filter((v) => v.numberOfResults > 0).length > 0

  if (!hasDomain && !hasValues) return null

  if (hasDomain) {
    return (
      <SliderFacetGroup
        controller={controller}
        displayName={displayName}
      />
    )
  }

  return (
    <RangesFacetGroup
      controller={controller}
      displayName={displayName}
    />
  )
}

/**
 * DateFacet — renders date-range checkboxes.
 * Uses controller.toggleSelect(value) for selection/deselection.
 */
const DateFacetGroup = ({ controller }) => {
  const [state, setState] = useState(controller.state)

  useEffect(() => {
    setState(controller.state)
    return controller.subscribe(() => setState(controller.state))
  }, [controller])

  const displayName = state.displayName ?? state.facetId
  const filteredValues = state.values.filter((v) => v.numberOfResults > 0)
  if (filteredValues.length === 0) return null

  return (
    <section className="c-search-filter__group o-grid">
      <div className="c-search-filter__group__label o-grid__item u-1/1">
        {t(displayName)}
      </div>
      <ul className="c-search-filter__group__container o-grid__item u-1/1 o-grid">
        {filteredValues.map((value) => {
          const key = `${state.field}:${value.start}-${value.end}`
          const isSelected = value.state === 'selected'
          const label = `${value.start} to ${value.end}`
          return (
            <li key={key} className="o-grid__item u-1/1">
              <Field
                name={key}
                fieldComponent="Checkbox"
                checkboxLabel={
                  value.numberOfResults
                    ? `${label} (${value.numberOfResults})`
                    : label
                }
                checked={isSelected}
                onChange={() => controller.toggleSelect(value)}
              />
            </li>
          )
        })}
      </ul>
    </section>
  )
}

/**
 * CategoryFacet — renders hierarchical category navigation.
 * Uses controller.toggleSelect(value) for selection/deselection.
 */
const CategoryFacetGroup = ({ controller, setFilter }) => {
  const [state, setState] = useState(controller.state)

  useEffect(() => {
    setState(controller.state)
    return controller.subscribe(() => setState(controller.state))
  }, [controller])

  const displayName = state.displayName ?? state.facetId
  const field = state.field

  if (state.hasActiveValues && state.selectedValueAncestry) {
    const ancestry = state.selectedValueAncestry
    const activeValueChildren = ancestry[ancestry.length - 1]?.children ?? []

    return (
      <section className="c-search-filter__group o-grid">
        <div className="c-search-filter__group__label o-grid__item u-1/1">
          {t(displayName)}
        </div>
        <ul className="c-search-filter__group__container o-grid__item u-1/1 o-grid">
          {/* Show selected breadcrumb path */}
          {ancestry.map((ancestryValue) => (
            <li key={`${field}:${ancestryValue.value}`} className="o-grid__item u-1/1">
              <Button
                className="u-text-bold c-category-facet"
                color="inline-link"
                onClick={() => setFilter({ group: field, val: ancestryValue.value })}
              >
                {ancestryValue.numberOfResults
                  ? `← ${ancestryValue.value} (${ancestryValue.numberOfResults})`
                  : `← ${ancestryValue.value}`}
              </Button>
            </li>
          ))}
          {/* Show children of selected category */}
          {activeValueChildren.map((child) => (
            <li key={`${field}:${child.value}`} className="o-grid__item u-1/1">
              <Button
                className="u-text-bold c-category-facet"
                color="inline-link"
                onClick={() => controller.toggleSelect(child)}
              >
                {child.numberOfResults
                  ? `${child.value} (${child.numberOfResults})`
                  : child.value}
              </Button>
            </li>
          ))}
        </ul>
      </section>
    )
  }

  // Root values (no active selection)
  const rootValues = state.values.filter((v) => v.numberOfResults > 0)
  if (rootValues.length === 0) return null

  return (
    <section className="c-search-filter__group o-grid">
      <div className="c-search-filter__group__label o-grid__item u-1/1">
        {t(displayName)}
      </div>
      <ul className="c-search-filter__group__container o-grid__item u-1/1 o-grid">
        {rootValues.map((value) => (
          <li key={`${field}:${value.value}`} className="o-grid__item u-1/1">
            <Button
              className="u-text-bold c-category-facet"
              color="inline-link"
              onClick={() => controller.toggleSelect(value)}
            >
              {value.numberOfResults
                ? `${value.value} (${value.numberOfResults})`
                : value.value}
            </Button>
          </li>
        ))}
      </ul>
    </section>
  )
}

// ---------------------------------------------------------------------------
// Main CoveoFacets component
// ---------------------------------------------------------------------------

/**
 * CoveoFacets renders all dynamic facets from the Coveo Commerce FacetGenerator.
 *
 * Props:
 *  - facetGenerator: the Coveo Commerce facetGenerator controller
 *  - showFilters: boolean controlling mobile panel visibility
 *  - toggleFilters: callback to dismiss mobile panel
 *  - instockOnly / outOfStockOnly / setStockFilter: in-stock toggle (optional)
 */
export const CoveoFacets = ({
  facetGenerator,
  showFilters,
  toggleFilters,
  instockOnly,
  outOfStockOnly,
  setStockFilter,
}) => {
  const [facets, setFacets] = useState([])
  const { setFilter } = useContext(SearchContext)

  useEffect(() => {
    const updateFacets = () => {
      const current = parseFacets(facetGenerator.facets)
      setFacets(current)
    }
    // Subscribe first — subscribe() calls listener immediately in Coveo headless
    const unsub = facetGenerator.subscribe(updateFacets)
    return unsub
  }, [facetGenerator])

  // Also subscribe to the engine directly as a fallback — some state changes
  // (e.g., individual facet value loading) don't trigger facetGenerator's
  // state change (which only tracks the facet IDs list).
  useEffect(() => {
    if (!facetGenerator || !facetGenerator.subscribe) return
    // Poll once after a short delay as a safety net for race conditions
    const timer = setTimeout(() => {
      const current = parseFacets(facetGenerator.facets)
      if (current.length > 0 && facets.length === 0) {
        console.log('[CoveoFacets] delayed update caught missed facets', current.length)
        setFacets(current)
      }
    }, 500)
    return () => clearTimeout(timer)
  }, [facetGenerator, facets.length])



  const renderFacet = (facet) => {

    switch (facet.type) {
      case 'regular':
        return <RegularFacetGroup key={facet.state.facetId} controller={facet} />
      case 'numericalRange':
        return <NumericFacetGroup key={facet.state.facetId} controller={facet} />
      case 'dateRange':
        return <DateFacetGroup key={facet.state.facetId} controller={facet} />
      case 'hierarchical':
        return <CategoryFacetGroup key={facet.state.facetId} controller={facet} setFilter={setFilter} />
      default:
        return null
    }
  }

  return (
    <section
      className={cn('c-search-filter', {
        'c-search-filter-show': showFilters,
      })}
    >
      <header className="o-grid">
        <div className="c-search-filter__close">
          <Button
            color="none"
            size="small"
            icon="close"
            onClick={toggleFilters}
            aria-label={t('Close')}
          />
        </div>
        <div className="o-grid__item u-1/1">
          <Button
            color="primary"
            className="c-search-filter__apply-filters c-button--block"
            onClick={toggleFilters}
          >
            {t('Apply filters')}
          </Button>
        </div>
        {!outOfStockOnly && setStockFilter && (
          <div className="c-search-filter__show-in-stock o-grid__item u-1/1">
            <Field
              name="in-stock-only"
              fieldComponent="Checkbox"
              checkboxLabel={t('Show in stock only')}
              checked={instockOnly}
              onChange={setStockFilter}
            />
          </div>
        )}
      </header>
      {facets.map(renderFacet)}
    </section>
  )
}

export default CoveoFacets
